/*
 * Copyright (c) 2026 Gabriele Bonora. Tutti i diritti riservati.
 *
 * Software proprietario. Nessuna licenza d'uso, copia, modifica o distribuzione
 * e' concessa in assenza di autorizzazione scritta del titolare dei diritti.
 * Vedere il file LICENSE nella radice del progetto.
 */

/*
 * Le fatture per periodo di un intervento (migrazione 014), nella scheda Conto.
 *
 * Un cantiere lungo si fattura a pezzi: un acconto per periodo, poi il saldo
 * (totale meno acconti). Ogni riga segue la sua fattura da "da emettere" a
 * "pagata", e una scadenza passata senza pagamento si vede in rosso: e' il motivo
 * per cui si fattura a pezzi invece che tutto alla fine.
 *
 * Questa NON e' la fattura elettronica: qui si registra cosa si e' fatturato, il
 * documento vero lo emette il programma di fatturazione e il suo numero si
 * scrive qui.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Check, Send, Undo2, ChevronDown, ChevronRight, AlertTriangle, Printer } from 'lucide-react';
import { interventiAPI } from '../api/interventi';
import { fmtGiorno, isoLocale } from '../utils/periodo';
import { apriRegistro } from '../utils/resoconto';

const STATO = {
  da_emettere: { label: 'Da emettere', bg: '#F3F4F6', color: '#374151' },
  emessa:      { label: 'Emessa, da incassare', bg: '#DBEAFE', color: '#1E40AF' },
  pagata:      { label: 'Pagata', bg: '#D1FAE5', color: '#065F46' },
};

const eur = (v) => (v === null || v === undefined ? '—' : `€ ${Number(v).toFixed(2)}`);
const ore = (v) => `${Number(v ?? 0).toFixed(2).replace('.', ',')} h`;

function piuGiorni(iso, n) {
  const [y, m, g] = iso.split('-').map(Number);
  return isoLocale(new Date(y, m - 1, g + n, 12));
}

export default function FatturazioniIntervento({ intervento, periodo }) {
  const queryClient = useQueryClient();
  const chiave = ['fatturazioni-intervento', intervento.id];
  const [errore, setErrore] = useState('');
  const [avvisi, setAvvisi] = useState([]);
  const [inModifica, setInModifica] = useState(null); // { riga, modo: 'emessa'|'pagata' }
  const [aperta, setAperta] = useState(null);

  const { data, isLoading, error } = useQuery({
    queryKey: chiave,
    queryFn: () => interventiAPI.fatturazioni(intervento.id),
    retry: false,
  });

  const dopo = (res) => {
    setErrore('');
    setAvvisi(res?.avvisi || []);
    setInModifica(null);
    queryClient.invalidateQueries({ queryKey: chiave });
    queryClient.invalidateQueries({ queryKey: ['intervento', String(intervento.id)] });
  };
  const sbaglio = (e) => setErrore(e.response?.data?.error || 'Operazione non riuscita');

  const registra = useMutation({
    mutationFn: (payload) => interventiAPI.registraFatturazione(intervento.id, payload),
    onSuccess: dopo, onError: sbaglio,
  });
  const aggiorna = useMutation({
    mutationFn: ({ fid, payload }) => interventiAPI.aggiornaFatturazione(intervento.id, fid, payload),
    onSuccess: dopo, onError: sbaglio,
  });
  const elimina = useMutation({
    mutationFn: (fid) => interventiAPI.eliminaFatturazione(intervento.id, fid),
    onSuccess: dopo, onError: sbaglio,
  });

  if (isLoading) return <div style={s.vuoto}>Caricamento delle fatture...</div>;
  if (error) return <div style={s.vuoto}>{error.response?.data?.error || 'Errore nel caricamento delle fatture'}</div>;

  const { fatturazioni, riepilogo: r, prossimo_periodo: prossimo, oggi } = data;
  const occupato = registra.isPending || aggiorna.isPending || elimina.isPending;

  // Il periodo scelto nel filtro, tagliato a oggi: si fattura solo il gia' fatto.
  let periodoFiltro = null;
  if (periodo && periodo.dal <= oggi && !r.ha_saldo) {
    const al = periodo.al > oggi ? oggi : periodo.al;
    const accavallato = fatturazioni.some((f) => f.tipo === 'acconto' && periodo.dal <= f.periodo_al && al >= f.periodo_dal);
    if (!accavallato) periodoFiltro = { dal: periodo.dal, al };
  }
  const stessoDelProssimo = prossimo && periodoFiltro && prossimo.dal === periodoFiltro.dal && prossimo.al === periodoFiltro.al;

  const acconto = (p) => registra.mutate({ tipo: 'acconto', periodo_dal: p.dal, periodo_al: p.al });

  return (
    <div style={s.box}>
      <h3 style={s.titolo}>Fatture per periodo</h3>
      <p style={s.spiega}>
        Per i lavori lunghi: fattura un acconto ogni mese (o quando serve) invece di tutto
        alla fine. Il saldo finale e' il totale meno gli acconti. Qui si registra cosa e'
        fatturato e se e' stato pagato; il documento lo emetti dal programma di fatturazione.
      </p>

      <div style={s.cards}>
        <Card label="Valore dell'intervento" valore={eur(r.totale_intervento)} />
        <Card label="Gia' fatturato" valore={eur(r.registrato)} />
        <Card label="Incassato" valore={eur(r.incassato)} colore="#065F46" />
        <Card label="Da incassare" valore={eur(r.da_incassare)} colore="#1E40AF" />
        {r.scaduto > 0 && <Card label="Scaduto, non pagato" valore={eur(r.scaduto)} colore="#B91C1C" forte />}
        <Card label="Ancora da fatturare" valore={eur(r.residuo)} />
      </div>

      {errore && <div style={s.errore}>{errore}</div>}
      {avvisi.length > 0 && (
        <div style={s.avviso}>
          <AlertTriangle size={15} />
          <div>Registrato, ma il conto era incompleto: {avvisi.join(' ')}</div>
        </div>
      )}

      <div style={s.azioni}>
        {periodoFiltro && (
          <button style={s.btnPrimario} disabled={occupato} onClick={() => acconto(periodoFiltro)}>
            <Plus size={15} /> Fattura il periodo filtrato ({fmtGiorno(periodoFiltro.dal)} – {fmtGiorno(periodoFiltro.al)})
          </button>
        )}
        {prossimo && !r.ha_saldo && !stessoDelProssimo && (
          <button style={periodoFiltro ? s.btnSecondario : s.btnPrimario} disabled={occupato} onClick={() => acconto(prossimo)}>
            <Plus size={15} /> Acconto dal {fmtGiorno(prossimo.dal)} al {fmtGiorno(prossimo.al)}
          </button>
        )}
        {!r.ha_saldo && r.residuo > 0 && (
          <button
            style={s.btnSecondario}
            disabled={occupato}
            onClick={() => {
              if (intervento.stato !== 'completato'
                && !window.confirm("L'intervento non e' ancora completato. Registrare comunque il saldo finale?")) return;
              registra.mutate({ tipo: 'saldo' });
            }}
          >
            <Check size={15} /> Saldo finale ({eur(r.residuo)})
          </button>
        )}
      </div>

      {fatturazioni.length === 0 ? (
        <div style={s.vuoto}>Nessuna fattura registrata per questo intervento.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={s.tabella}>
            <thead>
              <tr>
                <th style={s.th}></th>
                <th style={s.th}>Periodo</th>
                <th style={{ ...s.th, textAlign: 'right' }}>Ore</th>
                <th style={{ ...s.th, textAlign: 'right' }}>Importo</th>
                <th style={s.th}>Fattura</th>
                <th style={s.th}>Stato</th>
                <th style={s.th}></th>
              </tr>
            </thead>
            <tbody>
              {fatturazioni.map((f) => {
                const st = STATO[f.stato];
                const cambiato = f.valore_oggi !== undefined && f.valore_oggi !== null
                  && Math.abs(f.valore_oggi - f.importo_totale) >= 0.01;
                return [
                  <tr key={f.id} style={s.tr}>
                    <td style={s.td}>
                      <button style={s.icona} onClick={() => setAperta(aperta === f.id ? null : f.id)} aria-label="Dettaglio">
                        {aperta === f.id ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                      </button>
                    </td>
                    <td style={s.td}>
                      <strong>{f.tipo === 'saldo' ? 'Saldo' : 'Acconto'}</strong>
                      <span style={s.sotto}>{fmtGiorno(f.periodo_dal)} – {fmtGiorno(f.periodo_al)}</span>
                      {cambiato && (
                        <span style={s.cambiato}>oggi questo periodo varrebbe {eur(f.valore_oggi)}</span>
                      )}
                    </td>
                    <td style={{ ...s.td, textAlign: 'right' }}>{ore(f.ore)}</td>
                    <td style={{ ...s.td, textAlign: 'right', fontWeight: 700 }}>{eur(f.importo_totale)}</td>
                    <td style={s.td}>
                      {f.numero_fattura ? <>n. {f.numero_fattura}<span style={s.sotto}>del {fmtGiorno(f.data_emissione)}</span></> : '—'}
                      {f.scadenza && <span style={{ ...s.sotto, ...(f.scaduta ? s.scaduta : {}) }}>scade il {fmtGiorno(f.scadenza)}</span>}
                      {f.pagata_il && <span style={s.sotto}>pagata il {fmtGiorno(f.pagata_il)}</span>}
                    </td>
                    <td style={s.td}>
                      <span style={{ ...s.badge, backgroundColor: f.scaduta ? '#FEE2E2' : st.bg, color: f.scaduta ? '#B91C1C' : st.color }}>
                        {f.scaduta ? 'Scaduta, non pagata' : st.label}
                      </span>
                    </td>
                    <td style={{ ...s.td, whiteSpace: 'nowrap' }}>
                      {/* Il dettaglio di quei giorni, da allegare alla fattura: stessa
                          regola del giorno del conto, quindi le stesse ore. */}
                      <button style={s.icona} title="Registro lavori di questo periodo (PDF)" aria-label="Registro lavori del periodo"
                        onClick={() => apriRegistro({ dal: f.periodo_dal, al: f.periodo_al, intervento: intervento.id, materiali: true, importi: true })
                          .catch((e) => setErrore(e.message))}>
                        <Printer size={15} />
                      </button>
                      {f.stato === 'da_emettere' && (
                        <>
                          <button style={s.btnPiccolo} disabled={occupato} onClick={() => setInModifica({ riga: f, modo: 'emessa' })}>
                            <Send size={13} /> Emessa
                          </button>
                          <button style={s.icona} disabled={occupato} aria-label="Elimina"
                            onClick={() => window.confirm('Eliminare questa registrazione?') && elimina.mutate(f.id)}>
                            <Trash2 size={15} />
                          </button>
                        </>
                      )}
                      {f.stato === 'emessa' && (
                        <>
                          <button style={s.btnPiccolo} disabled={occupato} onClick={() => setInModifica({ riga: f, modo: 'pagata' })}>
                            <Check size={13} /> Pagata
                          </button>
                          <button style={s.icona} disabled={occupato} aria-label="Rimetti da emettere" title="Rimetti da emettere"
                            onClick={() => window.confirm('Rimettere la fattura "da emettere"? Resta in cronologia.')
                              && aggiorna.mutate({ fid: f.id, payload: { stato: 'da_emettere' } })}>
                            <Undo2 size={15} />
                          </button>
                        </>
                      )}
                      {f.stato === 'pagata' && (
                        <button style={s.icona} disabled={occupato} aria-label="Annulla pagamento" title="Annulla il pagamento"
                          onClick={() => window.confirm('Segnare di nuovo come non pagata?')
                            && aggiorna.mutate({ fid: f.id, payload: { stato: 'emessa' } })}>
                          <Undo2 size={15} />
                        </button>
                      )}
                    </td>
                  </tr>,
                  aperta === f.id && (
                    <tr key={`${f.id}-d`}>
                      <td colSpan={7} style={s.dettaglio}><Dettaglio f={f} /></td>
                    </tr>
                  ),
                ];
              })}
            </tbody>
          </table>
        </div>
      )}

      {inModifica && (
        <ModaleStato
          {...inModifica}
          oggi={oggi}
          occupato={aggiorna.isPending}
          onChiudi={() => setInModifica(null)}
          onSalva={(payload) => aggiorna.mutate({ fid: inModifica.riga.id, payload })}
        />
      )}
    </div>
  );
}

function Card({ label, valore, colore, forte }) {
  return (
    <div style={{ ...s.card, ...(forte ? { borderColor: '#FCA5A5', backgroundColor: '#FEF2F2' } : {}) }}>
      <div style={s.cardLabel}>{label}</div>
      <div style={{ ...s.cardValore, color: colore || '#111827' }}>{valore}</div>
    </div>
  );
}

// Il conto congelato al momento della registrazione: e' quello da ricopiare in fattura.
function Dettaglio({ f }) {
  const c = f.dettaglio;
  if (!c) return <span style={s.sotto}>Nessun dettaglio salvato.</span>;
  return (
    <div style={{ fontSize: '0.83rem', lineHeight: 1.6 }}>
      {f.tipo === 'saldo' && (
        <div style={{ marginBottom: '0.4rem', color: '#4B5563' }}>
          Valore complessivo dell'intervento {eur(c.costo_totale)}, meno gli acconti registrati prima.
        </div>
      )}
      {(c.manodopera || []).filter((m) => m.ore > 0).map((m) => (
        <div key={m.tecnico_id}>{m.nome}: {ore(m.ore)} — {eur(m.prezzo)}</div>
      ))}
      <div>Materiali: {eur(c.ricavo_materiali)}</div>
      {c.rimborso_km > 0 && <div>Rimborso chilometrico: {eur(c.rimborso_km)}</div>}
      {f.note && <div style={{ marginTop: '0.3rem' }}>Note: {f.note}</div>}
      {f.creata_da_nome && <div style={{ color: '#6B7280' }}>Registrata da {f.creata_da_nome}</div>}
      <div style={{ color: '#6B7280' }}>Importi IVA esclusa.</div>
    </div>
  );
}

function ModaleStato({ riga, modo, oggi, occupato, onChiudi, onSalva }) {
  const [numero, setNumero] = useState(riga.numero_fattura || '');
  const [emissione, setEmissione] = useState(riga.data_emissione || oggi);
  const [scadenza, setScadenza] = useState(riga.scadenza || piuGiorni(riga.data_emissione || oggi, 30));
  const [pagata, setPagata] = useState(riga.pagata_il || oggi);

  const salva = () => {
    if (modo === 'emessa') onSalva({ stato: 'emessa', numero_fattura: numero, data_emissione: emissione, scadenza: scadenza || null });
    else onSalva({ stato: 'pagata', pagata_il: pagata });
  };

  return (
    <div style={s.overlay} onClick={onChiudi}>
      <div style={s.modale} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 0.8rem', fontSize: '1rem' }}>
          {modo === 'emessa' ? 'Fattura emessa' : 'Pagamento ricevuto'} · {eur(riga.importo_totale)}
        </h3>
        {modo === 'emessa' ? (
          <>
            <label style={s.label}>Numero della fattura</label>
            <input style={s.input} value={numero} onChange={(e) => setNumero(e.target.value)} placeholder="es. 42/2026" autoFocus />
            <label style={s.label}>Data di emissione</label>
            <input style={s.input} type="date" value={emissione} onChange={(e) => setEmissione(e.target.value)} />
            <label style={s.label}>Scadenza del pagamento</label>
            <input style={s.input} type="date" value={scadenza} onChange={(e) => setScadenza(e.target.value)} />
            <div style={s.hint}>Passata la scadenza senza pagamento, la fattura compare in rosso qui e nell'elenco Fatturazione.</div>
          </>
        ) : (
          <>
            <label style={s.label}>Pagata il</label>
            <input style={s.input} type="date" value={pagata} onChange={(e) => setPagata(e.target.value)} autoFocus />
          </>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
          <button style={s.btnSecondario} onClick={onChiudi}>Annulla</button>
          <button style={s.btnPrimario} disabled={occupato || (modo === 'emessa' && !numero.trim())} onClick={salva}>
            {occupato ? 'Salvataggio...' : 'Salva'}
          </button>
        </div>
      </div>
    </div>
  );
}

const s = {
  box: { marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid #E5E7EB' },
  titolo: { fontSize: '0.95rem', fontWeight: 700, margin: '0 0 0.4rem', color: '#111827' },
  spiega: { fontSize: '0.82rem', color: '#6B7280', margin: '0 0 1rem', lineHeight: 1.5, maxWidth: '680px' },
  cards: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '0.6rem', marginBottom: '1rem' },
  card: { border: '1px solid #E5E7EB', borderRadius: '10px', padding: '0.65rem 0.8rem' },
  cardLabel: { fontSize: '0.72rem', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.03em', fontWeight: 600 },
  cardValore: { fontSize: '1.05rem', fontWeight: 700, marginTop: '0.2rem' },
  azioni: { display: 'flex', gap: '0.6rem', flexWrap: 'wrap', marginBottom: '1rem' },
  btnPrimario: { display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.55rem 1rem', backgroundColor: '#8B5CF6', color: 'white', border: 'none', borderRadius: '10px', fontSize: '0.86rem', fontWeight: 600, cursor: 'pointer', minHeight: '40px' },
  btnSecondario: { display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.55rem 1rem', backgroundColor: '#F3F4F6', color: '#374151', border: 'none', borderRadius: '10px', fontSize: '0.86rem', fontWeight: 600, cursor: 'pointer', minHeight: '40px' },
  btnPiccolo: { display: 'inline-flex', alignItems: 'center', gap: '0.3rem', padding: '0.35rem 0.65rem', backgroundColor: '#F3E8FF', color: '#7C3AED', border: 'none', borderRadius: '8px', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', marginRight: '0.3rem' },
  icona: { border: 'none', background: 'none', cursor: 'pointer', color: '#6B7280', padding: '0.25rem', display: 'inline-flex', verticalAlign: 'middle' },
  tabella: { width: '100%', borderCollapse: 'collapse', minWidth: '640px' },
  th: { padding: '0.5rem 0.6rem', textAlign: 'left', fontSize: '0.72rem', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.03em', backgroundColor: '#F9FAFB' },
  tr: { borderTop: '1px solid #F3F4F6' },
  td: { padding: '0.6rem', fontSize: '0.86rem', verticalAlign: 'top' },
  sotto: { display: 'block', fontSize: '0.75rem', color: '#6B7280', marginTop: '0.1rem' },
  scaduta: { color: '#B91C1C', fontWeight: 700 },
  cambiato: { display: 'block', fontSize: '0.74rem', color: '#1E40AF', marginTop: '0.15rem' },
  badge: { display: 'inline-block', padding: '0.2rem 0.55rem', borderRadius: '6px', fontSize: '0.76rem', fontWeight: 700 },
  dettaglio: { padding: '0.6rem 1rem 0.9rem 2.2rem', backgroundColor: '#FAFAFA' },
  vuoto: { padding: '1rem 0', color: '#6B7280', fontSize: '0.88rem' },
  errore: { padding: '0.6rem 0.75rem', backgroundColor: '#FEE2E2', color: '#991B1B', borderRadius: '8px', fontSize: '0.85rem', marginBottom: '0.8rem' },
  avviso: { display: 'flex', gap: '0.5rem', padding: '0.6rem 0.75rem', backgroundColor: '#FEF3C7', color: '#92400E', borderRadius: '8px', fontSize: '0.83rem', marginBottom: '0.8rem' },
  overlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(17,24,39,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', zIndex: 1000 },
  modale: { backgroundColor: 'white', borderRadius: '12px', padding: '1.25rem', width: '100%', maxWidth: '420px' },
  label: { display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginTop: '0.7rem', marginBottom: '0.25rem' },
  input: { width: '100%', padding: '0.6rem 0.7rem', border: '1px solid #E5E7EB', borderRadius: '10px', fontSize: '0.92rem', boxSizing: 'border-box', fontFamily: 'inherit' },
  hint: { fontSize: '0.76rem', color: '#6B7280', marginTop: '0.5rem', lineHeight: 1.5 },
};
